import { imageSize, type Size } from "./image-size"

// Real still photos where the browser can take them (ImageCapture: Android
// Chrome). A still goes through the phone's photo pipeline (its own focus,
// exposure and noise reduction) instead of being a frame of video. It's
// asked for at the sensor's 4:3 and then cut to the live picture's shape
// (prepare-photo.ts), so the photo is exactly what the person framed.
// Everywhere else (iPhone), and whenever a still fails, comes back the
// wrong shape or takes too long, the camera takes the sharpest of a short
// burst of video frames (use-camera.ts).

export type { Size }

export const STILL = {
  // Ask for about this wide (12 MP class phones offer ~4000; the upload
  // copy is 2048 on its long edge, so more would only cost time).
  TARGET_WIDTH: 3072,
  // The sensor's shape (width over height, held sideways).
  SENSOR_ASPECT: 4 / 3,
  // Past this, the burst instead (some phones stall on a still).
  TIMEOUT_MS: 1500,
} as const

export interface PhotoRange {
  min: number
  max: number
  step?: number
}

// A value on the camera's grid (rounded up where it fits), or null.
function onGrid(range: PhotoRange, want: number): number {
  const min = range.min > 0 ? range.min : 0
  const step = range.step && range.step > 0 ? range.step : 1
  const clamped = Math.min(range.max, Math.max(min, want))
  const up = min + Math.ceil((clamped - min) / step) * step
  return Math.round(up <= range.max ? up : min + Math.floor((clamped - min) / step) * step)
}

// The photo width to ask for: near the target, within what the camera
// offers, never under the live picture's long edge (no gain then). null =
// no still.
export function pickPhotoWidth(range: PhotoRange | undefined, streamLongEdge: number, target: number = STILL.TARGET_WIDTH): number | null {
  if (!range || !(range.max > 0) || !(streamLongEdge > 0)) return null
  if (range.max < streamLongEdge) return null
  const width = onGrid(range, Math.max(target, streamLongEdge))
  return width >= streamLongEdge ? width : null
}

// The height to go with it: the sensor's 4:3, so the camera can't pick a
// square or a cropped size that happens to be as wide.
export function pickPhotoHeight(range: PhotoRange | undefined, width: number): number | null {
  if (!range || !(range.max > 0)) return null
  return onGrid(range, Math.round(width / STILL.SENSOR_ASPECT))
}

// The still covers the picture the person framed: held the same way as the
// live picture (a phone that hands back a sideways photo is caught here),
// no smaller, and a shape that holds all of it. The live picture is a
// middle cut of the sensor, so the still must be no squarer than the
// sensor (a square still has lost the ends) and no wider than the live
// picture (a wider one has lost the top and bottom).
export function stillMatchesStream(still: Size, stream: Size): boolean {
  if (!(still.w > 0 && still.h > 0 && stream.w > 0 && stream.h > 0)) return false
  if (still.w !== still.h && stream.w !== stream.h && still.w > still.h !== stream.w > stream.h) return false
  const ratio = (s: Size) => Math.max(s.w, s.h) / Math.min(s.w, s.h)
  const sr = ratio(stream)
  const pr = ratio(still)
  if (pr < Math.min(STILL.SENSOR_ASPECT, sr) * 0.98 || pr > sr * 1.02) return false
  return Math.max(still.w, still.h) >= Math.max(stream.w, stream.h) * 0.95
}

interface PhotoCapabilitiesLike {
  imageWidth?: PhotoRange
  imageHeight?: PhotoRange
  fillLightMode?: string[]
}
interface PhotoSettingsLike {
  imageWidth?: number
  imageHeight?: number
  fillLightMode?: string
}
interface ImageCaptureLike {
  getPhotoCapabilities(): Promise<PhotoCapabilitiesLike>
  takePhoto(settings?: PhotoSettingsLike): Promise<Blob>
}
type ImageCaptureCtor = new (track: MediaStreamTrack) => ImageCaptureLike

function within<T>(work: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("still-timeout")), ms)
    work.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

async function sizeOf(photo: Blob): Promise<Size | null> {
  const fromHeader = await imageSize(photo).catch(() => null)
  if (fromHeader) return fromHeader
  // A format the header reader doesn't know: decode it.
  try {
    const bitmap = await createImageBitmap(photo, { imageOrientation: "from-image" })
    const size = { w: bitmap.width, h: bitmap.height }
    bitmap.close()
    return size
  } catch {
    return null
  }
}

export interface StillResult {
  photo: Blob | null
  // A still was actually asked of the camera (and so may have upset it).
  tried: boolean
}

export interface StillCamera {
  // A still of what's on screen, or no photo (the burst then).
  take(stream: Size): Promise<StillResult>
  // Stop trying stills on this stream.
  giveUp(): void
}

// Stills for this camera track, or null where the browser has none.
export function stillCamera(track: MediaStreamTrack): StillCamera | null {
  const Ctor = (globalThis as { ImageCapture?: ImageCaptureCtor }).ImageCapture
  if (!Ctor || track.kind !== "video") return null
  let capture: ImageCaptureLike
  try {
    capture = new Ctor(track)
  } catch {
    return null
  }
  // Asked at the first photo (not while the camera is still starting).
  let caps: Promise<PhotoCapabilitiesLike | null> | null = null
  let off = false
  return {
    async take(stream) {
      if (off || track.readyState !== "live") return { photo: null, tried: false }
      caps ??= within(capture.getPhotoCapabilities(), STILL.TIMEOUT_MS).catch(() => null)
      const c = await caps
      const width = pickPhotoWidth(c?.imageWidth, Math.max(stream.w, stream.h))
      if (!width) {
        off = true
        return { photo: null, tried: false }
      }
      const height = pickPhotoHeight(c?.imageHeight, width)
      const settings: PhotoSettingsLike = {
        imageWidth: width,
        ...(height ? { imageHeight: height } : {}),
        // No flash: on a glossy car it's one big glare (the light button is
        // there for the dark).
        ...(c?.fillLightMode?.includes("off") ? { fillLightMode: "off" } : {}),
      }
      try {
        const photo = await within(capture.takePhoto(settings), STILL.TIMEOUT_MS)
        const size = await sizeOf(photo)
        if (size && stillMatchesStream(size, stream)) return { photo, tried: true }
      } catch {
        // fall through
      }
      off = true
      return { photo: null, tried: true }
    },
    giveUp() {
      off = true
    },
  }
}
